import React, { useState, useEffect, useRef, useContext } from "react";
import { Box, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import { useColourMode } from "../contexts/ColourContext";
import { AuthContext } from "../contexts/AuthContext";
import { hasAccess } from "../functions";

const Profile = ({
  profileImage,
  handleLogoutButton,
  firstName,
  email,
  accessLevel,
  path = "/settings",
  initials,
}) => {
  const { loggedIn, isLoggedIn } = useContext(AuthContext);
  const [expanded, setExpanded] = useState(false);
  const colourTheme = useTheme();
  const { accentColour } = useColourMode();
  const profileRef = useRef(null);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close the expanded box if clicked outside of the profile component
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    // Add event listener when expanded
    if (expanded) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup listener on component unmount or when expanded state changes
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded]);

  return (
    <Box
      ref={profileRef}
      sx={{
        position: "relative",
        cursor: "pointer",
        alignItems: "center",
      }}
      onClick={handleToggleExpand}
    >
      <Box
        sx={{
          height: 30,
          width: 30,
          backgroundColor: accentColour,
          borderRadius: "50%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <img
          src={profileImage}
          alt="profile"
          style={{
            objectFit: "cover",
            height: "100%",
            width: "100%",
          }}
        />
      </Box>

      {/* Expanded Content */}
      {expanded && (
        <Box
          sx={{
            position: "absolute",
            top: "100%", // Show the expanded box below the profile image
            right: 0,
            width: "350px", // Set width for the expanded box
            maxWidth: "100dvw",
            backgroundColor: colourTheme.palette.background.default,
            backdropFilter: "blur(1rem)",
            boxShadow: colourTheme.palette.background.boxShadow,
            borderRadius: "1rem",
            mt: 1,
            p: "0.5rem",
            zIndex: 100, // Ensures it overlays other content
            textAlign: "center",
          }}
        >
          {/* User Info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              flexDirection: "column",
              p: "1rem",
            }}
          >
            <Box sx={{ fontSize: "0.75rem" }}>{email}</Box>
            <Box
              sx={{
                height: 75,
                width: 75,
                borderRadius: "50%",
                overflow: "hidden",
                mt: "0.5rem",
                mb: "0.5rem",
              }}
            >
              <img
                src={profileImage}
                alt={initials}
                style={{
                  objectFit: "cover",
                  height: "100%",
                  width: "100%",
                }}
              />
            </Box>
            <Box>
              <Box sx={{ fontSize: "1.25rem" }}>Hi, {firstName}</Box>
              <Box sx={{ fontSize: "0.65rem", fontWeight: "700" }}>
                {accessLevel.toUpperCase()}
              </Box>
            </Box>
            {/* Manage Account Button */}
            {loggedIn &&
              hasAccess(isLoggedIn.accessLevel, [
                "admin",
                "data",
                "user",
                "omni",
              ]) && (
                <Box
                  component={Link}
                  to={path}
                  sx={{
                    mt: 2,
                    mb: 1,
                    p: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    border: `2px solid ${colourTheme.palette.text.primary}`,
                    borderRadius: "1rem",
                    textDecoration: "none",
                    color: colourTheme.palette.text.primary,
                  }}
                >
                  Manage Account
                </Box>
              )}

            {/* Logout Button */}
            <Box onClick={handleLogoutButton}>Logout</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Profile;
